import { PlusOutlined } from '@ant-design/icons';
import { Tree } from 'antd';
import { DataNode } from 'antd/lib/tree';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useReferenceData } from '../../hooks/use_reference_data';
import { useRouter } from '../../hooks/use_router';
import { OmniaUrls } from '../../lib/urls';
import { getRoutePathByPathname, RoutePaths } from '../../routes';
import styles from './shop_nav.less';

enum KeyPrefix {
  CATEGORY = 'Category',
  SUBCATEGORY = 'Subcategory',
  SUBCAT_TYPE = 'SubcatType',
}

export const ShopNav = () => {
  const { allCategories } = useReferenceData();
  const { pathname } = useRouter();
  const routePath = getRoutePathByPathname(pathname);

  const subpaths = pathname.split('/');
  const pathStartsWithCategories = routePath.key === 'CATEGORIES';
  const isCategory = pathStartsWithCategories && subpaths.length === 3;
  const isSubcategory = pathStartsWithCategories && subpaths.length === 4;
  const isSubcatType = pathStartsWithCategories && subpaths.length === 5;

  const defaultExpandedKeys: string[] = [];

  if (isCategory || isSubcategory || isSubcatType) {
    const category = allCategories.find((c) => c.url_path === subpaths[2]);
    if (category) {
      defaultExpandedKeys.push(`${KeyPrefix.CATEGORY}::${category.name}`);
    }
    if (isSubcategory || isSubcatType) {
      const subcategory = category?.subcategories.find((s) => s.url_path === subpaths[3]);
      if (subcategory) {
        defaultExpandedKeys.push(`${KeyPrefix.SUBCATEGORY}::${subcategory.name}`);
      }
    }
  }

  const treeData: DataNode[] = useMemo(() => {
    const data: DataNode[] = allCategories.map((category) => {
      const catUrl = OmniaUrls.category(category);
      return {
        title: (
          <Link
            to={catUrl}
            className={classNames(styles.link, { [styles.link__selected]: pathname === catUrl })}
          >
            {category.display_name}
          </Link>
        ),
        key: `${KeyPrefix.CATEGORY}::${category.name}`,
        children: category.subcategories
          .filter((s) => !s.nav_hidden)
          .map((subcategory) => {
            const subcatUrl = OmniaUrls.subcategory(category, subcategory);
            return {
              title: (
                <Link
                  to={subcatUrl}
                  className={classNames(styles.link, {
                    [styles.link__selected]: pathname === subcatUrl,
                  })}
                >
                  {subcategory.display_name}
                </Link>
              ),
              key: `${KeyPrefix.SUBCATEGORY}::${subcategory.name}`,
              children: subcategory.subcat_types
                .filter((sc) => !sc.nav_hidden)
                .map((subcat_type) => {
                  const subcatTypeUrl = OmniaUrls.subcatType(category, subcategory, subcat_type);
                  return {
                    title: (
                      <Link
                        to={subcatTypeUrl}
                        className={classNames(styles.link, {
                          [styles.link__selected]: pathname === subcatTypeUrl,
                        })}
                      >
                        {subcat_type.display_name}
                      </Link>
                    ),
                    key: `${KeyPrefix.SUBCAT_TYPE}::${subcat_type.name}`,
                  };
                }),
            };
          }),
      };
    });
    data.push(
      {
        title: (
          <Link
            to={RoutePaths.SALE}
            className={classNames(styles.link, {
              [styles.link__selected]: pathname === RoutePaths.SALE,
            })}
          >
            Bargain Bin
          </Link>
        ),
        key: 'sale',
      },
      {
        title: (
          <Link
            to={RoutePaths.SALE_PRO}
            className={classNames(styles.link, {
              [styles.link__selected]: pathname === RoutePaths.SALE_PRO,
            })}
          >
            PRO Sales
          </Link>
        ),
        key: 'sale_pro',
      },
      {
        title: (
          <Link
            to={RoutePaths.NEW}
            className={classNames(styles.link, {
              [styles.link__selected]: pathname === RoutePaths.NEW,
            })}
          >
            New Arrivals
          </Link>
        ),
        key: 'new_arrivals',
      },
      {
        title: (
          <Link
            to={RoutePaths.BEST_SELLERS}
            className={classNames(styles.link, {
              [styles.link__selected]: pathname === RoutePaths.BEST_SELLERS,
            })}
          >
            Best Sellers
          </Link>
        ),
        key: 'best_sellers',
      }
    );

    return data;
  }, [allCategories, pathname]);

  return (
    <nav className={styles.nav}>
      <Tree
        defaultExpandedKeys={defaultExpandedKeys}
        key={defaultExpandedKeys.join(',')} // pathname hasn't quite changed on first render, so it lags by a page. this forces a fresh render with new default expanded keys, which works fine for this component
        switcherIcon={<PlusOutlined />}
        selectable={false}
        treeData={treeData}
      />
    </nav>
  );
};
